import { AuthKitProvider } from '@workos-inc/authkit-react';
import { ThemeInit } from '../.flowbite-react/init';
import moment from 'moment';
import 'moment/locale/fr';
import { useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter } from 'react-router';
import 'react-toastify/dist/ReactToastify.css';
import ToastContainer from 'src/components/toast';
import { AuthProvider } from 'src/contexts/AuthContext';
import LoadingProvider from 'src/contexts/LoadingContext';
import LocationProvider from 'src/contexts/LocationContext';
import { OrganizationProvider } from 'src/contexts/OrganizationContext';
import { routePaths, TOKEN_KEY, WORKOS_USER_KEY } from 'src/utils/constants';
import { setCookie } from 'src/utils/cookie.util';
import i18next from './locales';
import Routes from './router/';
import { setLocalStorage } from 'src/utils/storage.util';
import LoadingProgressBar from 'components/loadingProgressBar.js';
import AuthRedirectOverlay from 'src/components/auth/AuthRedirectOverlay';
import { useSiteConfig } from 'src/contexts/SiteGuardContext';
import { MixpanelProvider } from 'src/analytics/MixpanelProvider';
import ErrorBoundary from 'src/components/ErrorBoundary';
import { ExperimentProvider } from 'src/experiments/ExperimentProvider';
import CustomFontsLoader from 'src/components/CustomFontsLoader';

// Set the global locale. `navigator` is undefined on the server, so the
// module would crash if we accessed it eagerly. Default to en-US there;
// SSR rendering doesn't depend on locale-aware moment formatting today.
const userLocale =
   typeof navigator !== 'undefined'
      ? navigator.language || navigator.languages?.[0] || 'en-US'
      : 'en-US';
moment.locale(userLocale);

function App() {
   const { siteConfig: _siteConfig } = useSiteConfig()
   const [isReady, setIsReady] = useState(false);

   const isLocal = window.location.hostname === 'localhost';

   const isUseGoogleTagManager = import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID;

   useEffect(() => {
      if (!isUseGoogleTagManager || isLocal) return;

      import('react-gtm-module').then(({ default: TagManager }) => {
         TagManager.initialize({ gtmId: `${import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID}` });
      });
   }, [isLocal, isUseGoogleTagManager]);

   // Synchronize document language attribute with i18n language for accessibility/screen readers
   useEffect(() => {
      const updateLangAttribute = () => {
         const langCode = i18next.language || 'en';
         const primaryLang = langCode.split('-')[0].toLowerCase();
         document.documentElement.lang = primaryLang;
      };

      updateLangAttribute();

      i18next.on('languageChanged', updateLangAttribute);
      i18next.on('initialized', updateLangAttribute);

      return () => {
         i18next.off('languageChanged', updateLangAttribute);
         i18next.off('initialized', updateLangAttribute);
      };
   }, []);

   // <title> + favicon used to be mutated imperatively here on every
   // siteConfig change. Removed in NPT-1072 — both are now emitted by
   // the SSR shell (pages/+Head.tsx for the favicon; per-page +title.ts
   // / +description.ts for <title> and meta description) and per-page
   // <SEO> components for the OG tags. The old imperative writes
   // OVERRODE the per-page SSR title with the brand default after
   // hydration, which silently broke per-page tab titles for routes
   // like /c/:slug/hotels/:id.

   // Check if calling back from WorkOS
   const isWorkOSCallback = window.location.search.includes('code=') && window.location.search.includes('state=');

   return (
      <div className="App">
         <ThemeInit />
         <CustomFontsLoader />
         <AuthKitProvider
            apiHostname={`${import.meta.env.VITE_AUTH_DOMAIN}`}
            clientId={`${import.meta.env.VITE_AUTH_CLIENT_ID}`}
            devMode={false}
            https={['development', 'staging', 'production'].includes(import.meta.env.MODE)}
            redirectUri={window.location.origin}
            onRedirectCallback={async params => {
               if (params) {
                  const { accessToken, user } = params;
                  if (accessToken && user) {
                     setCookie(TOKEN_KEY, accessToken, 1440);
                     setLocalStorage(WORKOS_USER_KEY, user);
                  }

                  // Check redirectUri
                  const { state } = params;
                  if (state && state.returnTo) {
                     if (state.returnTo && !state.returnTo.includes(routePaths.LOGIN_PAGE)) {
                        window.location.href = state.returnTo;
                     }
                  }
               } else {
                  setIsReady(true);
               }
            }}
         >
            {(isWorkOSCallback && isReady) || !isWorkOSCallback
               ? (
                  <LoadingProvider>
                     <I18nextProvider i18n={i18next}>
                        <LoadingProgressBar />
                        <BrowserRouter>
                           <MixpanelProvider>
                              <LocationProvider>
                                 <ExperimentProvider>
                                    <AuthProvider>
                                       <AuthRedirectOverlay />
                                       <OrganizationProvider>
                                          {/* Last line of defence: without it a render error
                                              unmounts the tree and leaves a blank page. */}
                                          <ErrorBoundary name="app" variant="page">
                                             <Routes />
                                          </ErrorBoundary>
                                       </OrganizationProvider>
                                    </AuthProvider>
                                 </ExperimentProvider>
                              </LocationProvider>
                           </MixpanelProvider>
                        </BrowserRouter>
                        <ToastContainer />
                     </I18nextProvider>
                  </LoadingProvider>
               )
               : null
            }
         </AuthKitProvider>
      </div>
   );
}

export default App;
